import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, FilePdf, CaretDown, CaretRight, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
  { value: "pending",     label: "Pending" },
  { value: "in_progress", label: "In progress" },
  { value: "completed",   label: "Completed" },
];

const statusPillClass = (s) => {
  if (s === "completed")   return "bg-[#DEFBE6] text-[#0E6027] border-[#A7F0BA]";
  if (s === "in_progress") return "bg-[#FFF8E1] text-[#8A6D00] border-[#F1C21B]";
  return "bg-[#F4F4F4] text-[#525252] border-[#E0E0E0]";
};

const CHECKPOINTS = [
  { key: "account_in_status",         label: "Account In" },
  { key: "audit_in_progress_status",  label: "Audit In Progress" },
  { key: "bank_confirmation_status",  label: "Bank Confirmation" },
  { key: "signing_release_status",    label: "Signing Release" },
  { key: "report_release_status",     label: "Report Release" },
];

const allComplete = (rec) => CHECKPOINTS.every(cp => rec[cp.key] === "completed");

const overallStatus = (rec) => {
  const values = CHECKPOINTS.map(cp => rec[cp.key] || "pending");
  if (values.every(v => v === "completed")) return "completed";
  if (values.every(v => v === "pending"))   return "pending";
  return "in_progress";
};

const OVERALL_LABEL = { completed: "Complete", pending: "Pending", in_progress: "In progress" };
const OVERALL_CLASS = {
  completed:   "bg-[#DEFBE6] text-[#0E6027] border-[#A7F0BA]",
  in_progress: "bg-[#FFF8E1] text-[#8A6D00] border-[#F1C21B]",
  pending:     "bg-[#F4F4F4] text-[#525252] border-[#E0E0E0]",
};

export default function AuditClients() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [rows, setRows] = useState([]);
  const [records, setRecords] = useState({}); // audit_client_id -> [records]
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const [genOpen, setGenOpen] = useState(false);
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genYears, setGenYears] = useState("1");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/clients");
      setRows(data || []);
      const results = await Promise.all((data || []).map(c =>
        api.get(`/audit/clients/${c.id}/records`).then(r => [c.id, r.data]).catch(() => [c.id, []])
      ));
      const map = {}; for (const [id, arr] of results) map[id] = arr;
      setRecords(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadListing = () => {
    const token = localStorage.getItem("cosec_token");
    window.open(`${API}/audit/clients/listing/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.ssm_no || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const k = (r.financial_year_end || "").trim() || "—";
      (g[k] = g[k] || []).push(r);
    }
    return Object.entries(g);
  }, [filtered]);

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const addFY = async (c) => {
    try {
      await api.post(`/audit/clients/${c.id}/records`, {});
      toast.success("Added next FY record");
      setExpanded(p => ({ ...p, [c.id]: true }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const addSpecificFY = async (c, fy) => {
    try {
      await api.post(`/audit/clients/${c.id}/records`, { financial_year: String(fy) });
      toast.success(`Added FY ${fy}`);
      setExpanded(p => ({ ...p, [c.id]: true }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const updateRecStatus = async (rec, field, value) => {
    setRecords(prev => {
      const arr = (prev[rec.audit_client_id] || []).map(r => r.id === rec.id ? { ...r, [field]: value } : r);
      return { ...prev, [rec.audit_client_id]: arr };
    });
    try {
      await api.patch(`/audit/records/${rec.id}/status`, { field, value });
    } catch (e) {
      toast.error("Failed to update status");
      load();
    }
  };

  const deleteRec = async (rec) => {
    if (!window.confirm(`Delete FY ${rec.financial_year} record?`)) return;
    await api.delete(`/audit/records/${rec.id}`);
    load();
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This also removes it from Tax Clients and any audit-year records.`)) return;
    try {
      if (c.source_tax_client_id) await api.delete(`/tax/clients/${c.source_tax_client_id}`);
      else await api.delete(`/audit/clients/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const bulkGenerate = async () => {
    const startYear = parseInt(genYear, 10);
    const count = parseInt(genYears, 10);
    if (!startYear || !count || count < 1) return toast.error("Enter a valid start year and count");
    setGenerating(true);
    let created = 0, skipped = 0, failed = 0;
    try {
      for (const c of rows) {
        const existing = records[c.id] || [];
        for (let i = 0; i < count; i++) {
          const fy = String(startYear + i);
          if (existing.some(x => String(x.financial_year) === fy)) { skipped++; continue; }
          try {
            await api.post(`/audit/clients/${c.id}/records`, { financial_year: fy });
            created++;
          } catch { failed++; }
        }
      }
      toast.success(`${created} created · ${skipped} existed · ${failed} failed`);
      setGenOpen(false); load();
    } finally { setGenerating(false); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="audit-clients-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Directory</div>
          <h1 className="text-4xl font-bold tracking-tight">Audit Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-synced from Tax Clients. Track bank confirmation, signing release and report release per financial year.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(true)}
                  data-testid="audit-bulk-gen-btn">
            <Sparkle size={16} weight="bold" className="mr-2" /> Bulk auto-generate
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={downloadListing}
                  data-testid="audit-listing-pdf-btn">
            <FilePdf size={16} weight="bold" className="mr-2" /> Client Listing PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input placeholder="Search by name or SSM no." value={q} onChange={e => setQ(e.target.value)}
               className="max-w-md rounded-sm" data-testid="audit-search" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {filtered.length} clients
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm h-8"
                  onClick={() => setExpanded(Object.fromEntries(rows.map(c => [c.id, true])))}>
            Expand all
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => setExpanded({})}>
            Collapse all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="border border-border">
          {grouped.map(([fye, group]) => (
            <div key={fye}>
              <div className="bg-[#F4F4F4] border-b border-border px-6 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                FYE {fye}  ·  {group.length} {group.length === 1 ? "client" : "clients"}
              </div>
              {group.map(c => {
                const recs = records[c.id] || [];
                const isOpen = !!expanded[c.id];
                return (
                  <div key={c.id} className="border-b border-border last:border-b-0"
                       data-testid={`audit-row-${c.id}`}>
                    <div className="grid grid-cols-[24px_2fr_1.2fr_0.7fr_120px_60px] items-center gap-3 px-6 py-3">
                      <button type="button" onClick={() => toggle(c.id)} data-testid={`audit-toggle-${c.id}`}>
                        {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                      </button>
                      <div className="min-w-0">
                        <div className="text-sm truncate font-medium">{c.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          {c.ssm_no || "—"}
                          {isAdmin && (c.owner_name || c.owner_email) && (
                            <span className="ml-2 normal-case tracking-normal text-[10px] text-[#0F62FE]" data-testid={`audit-owner-${c.id}`}>
                              · {c.owner_name || c.owner_email}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">FYE {c.financial_year_end || "—"}</div>
                      <div className="text-xs font-mono text-muted-foreground">
                        {recs.length} FY {recs.length === 1 ? "record" : "records"}
                      </div>
                      <Button size="sm" className="rounded-sm h-8 bg-[#0F62FE] hover:bg-[#0043CE]"
                              onClick={() => addFY(c)} data-testid={`audit-addfy-${c.id}`}>
                        <Plus size={13} weight="bold" className="mr-1" /> Next FY
                      </Button>
                      <div className="flex items-center justify-end">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-[#DA1E28]"
                                onClick={() => removeClient(c)} data-testid={`audit-del-${c.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="border-t border-border bg-[#FAFAFA]">
                        <div className="flex items-center justify-between px-6 py-2">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Audit records</span>
                          <div className="flex items-center gap-2">
                            <QuickYearSelect onPick={(y) => addSpecificFY(c, y)} testid={`audit-quickfy-${c.id}`} />
                          </div>
                        </div>
                        {recs.length === 0 ? (
                          <div className="px-6 py-6 text-xs text-muted-foreground">
                            No FY records yet. Click <b>Next FY</b> to add one.
                          </div>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left border-t border-b border-border">
                                <Th className="w-[100px]">FY</Th>
                                {CHECKPOINTS.map(cp => <Th key={cp.key}>{cp.label}</Th>)}
                                <Th className="w-[110px]">Overall</Th>
                                <Th className="w-[60px] text-right">Actions</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {recs.map(rec => {
                                const overall = overallStatus(rec);
                                return (
                                <tr key={rec.id}
                                    className={`border-b last:border-b-0 border-border ${overall === "completed" ? "bg-[#DEFBE6]/40" : ""}`}
                                    data-testid={`audit-rec-${rec.id}`}>
                                  <td className="px-6 py-2 font-mono">FY {rec.financial_year}</td>
                                  {CHECKPOINTS.map(cp => (
                                    <td key={cp.key} className="px-6 py-2">
                                      <Select value={rec[cp.key] || "pending"}
                                              onValueChange={v => updateRecStatus(rec, cp.key, v)}>
                                        <SelectTrigger
                                          className={`h-7 rounded-sm border text-[11px] font-mono uppercase tracking-wider w-[130px] ${statusPillClass(rec[cp.key])}`}
                                          data-testid={`audit-rec-${rec.id}-${cp.key}`}
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </td>
                                  ))}
                                  <td className="px-6 py-2" data-testid={`audit-rec-${rec.id}-overall`}>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-sm border font-mono text-[11px] uppercase tracking-wider ${OVERALL_CLASS[overall]}`}>
                                      {OVERALL_LABEL[overall]}
                                    </span>
                                  </td>
                                  <td className="px-6 py-2 text-right">
                                    <Button size="icon" variant="ghost" className="h-8 w-8 text-[#DA1E28]"
                                            onClick={() => deleteRec(rec)} data-testid={`audit-rec-del-${rec.id}`}>
                                      <Trash size={14} />
                                    </Button>
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Bulk auto-generate FY records</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Creates FY records with all statuses set to <b>Pending</b> for every audit client. Existing FYs are skipped.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Start Year</Label>
                <Input type="number" className="mt-1 rounded-sm" value={genYear}
                       onChange={e => setGenYear(e.target.value)}
                       data-testid="audit-gen-year-input" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">How many years?</Label>
                <Input type="number" min="1" max="10" className="mt-1 rounded-sm" value={genYears}
                       onChange={e => setGenYears(e.target.value)}
                       data-testid="audit-gen-count-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                    onClick={bulkGenerate} disabled={generating}
                    data-testid="audit-gen-confirm-btn">
              {generating ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuickYearSelect({ onPick, testid }) {
  const y = new Date().getFullYear();
  const opts = [
    { v: String(y - 1), label: `Last Year · ${y - 1}` },
    { v: String(y),     label: `This Year · ${y}` },
    { v: String(y + 1), label: `Next Year · ${y + 1}` },
  ];
  return (
    <Select onValueChange={onPick}>
      <SelectTrigger className="h-8 w-[170px] rounded-sm text-xs" data-testid={testid}>
        <SelectValue placeholder="+ Add specific FY" />
      </SelectTrigger>
      <SelectContent>
        {opts.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
